/**
 * @fileoverview Prompt template for character-arc-creation-v2.
 *
 * This prompt generates a detailed character arc defined as a sequence of
 * internal beats at key structural phases.
 *
 * Shared arc concept sections are imported from the canonical schema to stay
 * in sync with character-arc-extractor-v3.
 */

import {
  CHARACTER_ARC_CONCEPTS_PROMPT,
  STRUCTURE_PHASES_TABLE_PROMPT,
  ARC_PROGRESS_PROMPT,
} from '@/domains/characters/schemas';

import {
  ARC_BEAT_GUIDANCE_LONG,
  ARC_BEAT_GUIDANCE_SHORT,
  ARC_SUMMARY_GUIDANCE,
  ARC_NAMING,
  BEAT_TEXT_NORMS,
} from '@/ai/agents/shared/character-arc-guidance';

import { CHARACTER_ARC_DEFINITION } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert narrative designer and character architect.

## TASK INSTRUCTIONS

Your mission is to design a compelling, detailed, and psychologically coherent developmental arc for a single character, based on the provided story context.

${CHARACTER_ARC_DEFINITION}

### Analysis Steps

1. **Analyze the Character:** Review the active character's initial state, personality, and planned plot role. Identify their starting "Lie" — the false belief or flaw that will be challenged.

2. **Analyze the Context:** Review the story's blurb, plot, themes, and other existing characters to understand the world the character inhabits and the conflicts they will face.

3. **Design the Arc:** Create a single, detailed character arc as a sequence of **beats**—internal psychological shifts at key structural phases. Each beat must map an internal change, NOT a physical event.

**CRITICAL**: A character arc must strictly map an internal psychological shift. Do NOT produce a list of physical events — those belong to storylines and plot threads. Focus ~80% on what changes inside the character and ~20% on observable behavior that results from the shift.

### Character Arc Concepts

${CHARACTER_ARC_CONCEPTS_PROMPT}

### Structure Phases

${STRUCTURE_PHASES_TABLE_PROMPT}

### Arc Progress Values

${ARC_PROGRESS_PROMPT}

## OUTPUT REQUIREMENTS

${ARC_NAMING}

${ARC_SUMMARY_GUIDANCE}

${BEAT_TEXT_NORMS}

{{#if isShortForm}}
### SHORT-FORM MODE

This is a **short-form project**. For short stories, individual beat-by-beat phase tracking is overkill — the arc plays out in a single, compact narrative rather than across chapters.

${ARC_BEAT_GUIDANCE_SHORT}

You must output:

- **arcName**: A short, evocative title
- **arcSummary**: The arc summary IS the arc — see summary guidance above for appropriate length based on the character's category (major vs minor).
- **beats**: An **empty array** \`[]\` — beats are not used for short-form projects.

{{else}}
### LONG-FORM MODE

${ARC_BEAT_GUIDANCE_LONG}

You must output:

- **arcName**: A short, evocative title
- **arcSummary**: A brief summary (see summary guidance above — use the "long-form with beats" length)
- **beats**: An array of beats following the density targets above

Each beat must include:
- **id**: A unique UUID
- **globalPhase**: Where this beat sits in the OVERALL story timeline (one of the 7 phases)
- **arcPhase**: The structural function for THIS CHARACTER's internal arc (one of the 7 phases)
- **internalState**: The psychological shift (required)
- **behavior**: Observable action (optional but recommended)
- **arcProgress**: Number from -1 to +1

**CRITICAL — globalPhase vs arcPhase:**
- For characters whose arc spans the entire story: globalPhase equals arcPhase.
- For characters introduced late, killed early, or with compressed arcs: arcPhase DIFFERS from globalPhase. A character introduced at the global Midpoint would have their first beat with globalPhase="Midpoint" but arcPhase="Initial State".
- You MUST provide BOTH fields for every beat.

{{/if}}

<<<userMessage>>>

## PRIMARY TASK CONTEXT

**ACTIVE CHARACTER**
<<<activeCharacterContext>>>

## SECONDARY TASK CONTEXT

<<<storyContext>>>

<<<loreContext>>>

<<<seriesContext>>>

<<<characters>>>

<<<worldElements>>>

---

## OUTPUT INSTRUCTIONS

Provide your output in the required JSON format with \`plannedCharacterArc\` containing \`id\`, \`arcName\`, \`arcSummary\`, and \`beats\`.
`;
